import { ReactNode } from "react";
import t from "@/lang/lang";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { Button } from "@/shared/components/ui/button";
import { useListMasterDataPdfQuery } from "@/services/backend/company/api/master-data-pdf";
import { MasterDataPdfDeleteDialog } from "@/services/backend/company/components/master-data-pdf-delete-dialog";
import { MasterDataPdfActiveCheckbox } from "@/services/backend/company/components/master-data-pdf-active-checkbox";

/**
 * MasterDataPdfTable lists the PDF master data of one company.
 *
 * The link into the editor is injected so the table stays
 * router-agnostic.
 */
export function MasterDataPdfTable({
  companyId,
  renderRowLink,
}: {
  companyId: string;
  renderRowLink: (id: string, children: ReactNode) => ReactNode;
}) {
  const { data, isLoading, error } = useListMasterDataPdfQuery({
    company: { active: true, values: [companyId] },
  });

  return (
    <TableCard>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-0">{t("Aktiv")}</TableHead>
            <TableHead>{t("Bezeichnung")}</TableHead>
            <TableHead className="w-0" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && <TableLoadingRow colSpan={3} />}
          {error && <TableRtkErrorRow colSpan={3} error={error} />}
          {data && data.items.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground">
                {t(
                  "Für dieses Unternehmen sind keine PDF-Template-Daten hinterlegt.",
                )}
              </TableCell>
            </TableRow>
          )}
          {data?.items.map((masterData) => (
            <TableRow key={masterData.id}>
              <TableCell>
                <MasterDataPdfActiveCheckbox
                  companyId={companyId}
                  masterDataPdfId={masterData.id}
                />
              </TableCell>
              <TableCell className="py-0">
                {renderRowLink(
                  masterData.id,
                  <Button variant="link" size="sm">
                    {masterData.label}
                  </Button>,
                )}
              </TableCell>
              <TableCell>
                <MasterDataPdfDeleteDialog masterData={masterData} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableCard>
  );
}
